import { useCallback, useEffect } from "react"
import * as signalR from "@microsoft/signalr"
import { getOrCreateApplicationHubConnection } from "../infrastructure/signalr-service"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { CvProcessedDto } from "@/modules/recruitment/candidates/dtos/cv-upload"
import {
  ApplicationComment,
  ApplicationCommentDeleted,
  ApplicationCommentReactionSummary,
  MentionNotification,
} from "@/modules/recruitment/applications/dtos/ApplicationComment"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { vacancySuggestionKeys } from "@/modules/recruitment/suggestions/hooks/useVacancySuggestions"

interface VacancySuggestionResponseNotificationDto {
  suggestionId: string
  candidateName: string
  outcome: string
  targetVacancyTitle: string
  sourceVacancyTitle: string
}

function useHubEvent<T>(eventName: string) {
  const organization = useCurrentOrganization()

  return useCallback(
    (handler: (dto: T) => void): (() => void) => {
      if (!organization?.id) {
        return () => {}
      }

      const { connection, start } = getOrCreateApplicationHubConnection(
        organization.id,
      )

      if (connection.state === signalR.HubConnectionState.Disconnected) {
        start()
      }

      connection.on(eventName, handler)

      return () => {
        connection.off(eventName, handler)
      }
    },
    [organization?.id, eventName],
  )
}

export function useApplicationHub() {
  const organization = useCurrentOrganization()
  const queryClient = useQueryClient()

  const onCvProcessed = useHubEvent<CvProcessedDto>("CvProcessed")
  const onCommentPosted = useHubEvent<ApplicationComment>("ApplicationCommentPosted")
  const onCommentUpdated = useHubEvent<ApplicationComment>("ApplicationCommentUpdated")
  const onCommentDeleted = useHubEvent<ApplicationCommentDeleted>("ApplicationCommentDeleted")
  const onCommentReactionChanged = useHubEvent<ApplicationCommentReactionSummary>("ApplicationCommentReactionChanged")
  const onMentioned = useHubEvent<MentionNotification>("YouWereMentioned")

  useEffect(() => {
    if (!organization?.id) return

    const { connection, start } = getOrCreateApplicationHubConnection(organization.id)

    if (connection.state === signalR.HubConnectionState.Disconnected) {
      start()
    }

    const handler = (dto: VacancySuggestionResponseNotificationDto) => {
      if (dto.outcome === 'Accepted') {
        toast.success(
          `${dto.candidateName} accepted the suggestion for ${dto.targetVacancyTitle}`,
        )
      } else {
        toast.info(
          `${dto.candidateName} declined the suggestion for ${dto.targetVacancyTitle}`,
        )
      }
      queryClient.invalidateQueries({
        queryKey: vacancySuggestionKeys.all(organization.id),
      })
    }

    connection.on('VacancySuggestionResponded', handler)

    return () => {
      connection.off('VacancySuggestionResponded', handler)
    }
  }, [organization?.id, queryClient])

  return {
    onCvProcessed,
    onCommentPosted,
    onCommentUpdated,
    onCommentDeleted,
    onCommentReactionChanged,
    onMentioned,
  }
}
